import { useState, useEffect, useRef } from 'react';

export interface WsPriceData {
  usd: number;
  change24h: number;
  updatedAt: string;
}

export type PriceMap = Record<string, WsPriceData>;

export interface UseRealtimePricesReturn {
  prices: PriceMap;
  connected: boolean;
  lastUpdated: Date | null;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];

/**
 * Establishes a native WebSocket connection to the price oracle's /ws/prices endpoint.
 * Returns live prices, connection status, and last-updated timestamp.
 * Handles reconnect with exponential backoff (max 30 s) and disconnects on unmount.
 *
 * @param symbols Optional array of symbols to subscribe to (reduces payload size).
 *                If omitted, all available symbols are received.
 */
export function useRealtimePrices(symbols?: string[]): UseRealtimePricesReturn {
  const [prices, setPrices] = useState<PriceMap>({});
  const [connected, setConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Stable refs — avoid re-creating WebSocket on every render
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  // Keep latest symbols accessible inside event handlers without re-creating them
  const symbolsRef = useRef<string[] | undefined>(symbols);
  symbolsRef.current = symbols;

  useEffect(() => {
    mountedRef.current = true;

    // Skip connection entirely when no price oracle URL is configured (avoids
    // an infinite localhost reconnect loop in production deployments).
    const oracleUrl = import.meta.env.VITE_PRICE_ORACLE_URL;
    if (!oracleUrl) {
      return () => {
        mountedRef.current = false;
      };
    }

    const connect = () => {
      if (!mountedRef.current) return;

      // Derive WebSocket URL from VITE_PRICE_ORACLE_URL
      const baseUrl = oracleUrl.replace(/^https?/, 'ws').replace(/\/$/, '');
      const url = `${baseUrl}/ws/prices`;

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) {
          ws.close();
          return;
        }
        reconnectAttemptRef.current = 0;
        setConnected(true);

        // Send subscription filter if provided
        const subs = symbolsRef.current;
        if (subs && subs.length > 0) {
          ws.send(JSON.stringify({ subscribe: subs }));
        }
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data) as {
            type: string;
            prices: PriceMap;
          };
          if (data.type === 'price_update' && data.prices) {
            setPrices(data.prices);
            setLastUpdated(new Date());
          }
        } catch {
          // ignore unparseable frames
        }
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setConnected(false);
        wsRef.current = null;

        // Exponential backoff reconnect
        const delay =
          RECONNECT_DELAYS[
            Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1)
          ];
        reconnectAttemptRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // onerror is always followed by onclose, so let onclose handle reconnect
      };
    };

    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimeoutRef.current !== null) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    // Run once on mount; symbols changes are picked up via symbolsRef without reconnect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { prices, connected, lastUpdated };
}
